/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Component, useState, useEffect } from 'react'
import { TableList, TableListHeader } from '../../_shared/table-list';
import { Input, Icon, Checkbox, Modal } from 'antd';
import Row from '../../_shared/row';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { getAccounts } from '../../_shared/services/accounts.service';
import { get, set, upperFirst } from 'lodash';
import { Button } from '../../_shared/button';
import SStyles from '../../_shared/styles';
import colors from '../../_shared/colors';
import { columnText, exportToCsv } from '../../actions/utils';
import moment from 'moment';
import { Profile } from '../profile';
import { ROLES } from '../../models/account';
import { getUserById } from '../../_shared/services/manage-users.service';

// Returns the additional license numbers for an account as a comma-joined
// string (the `licenseNumbers` array minus the primary `License #`).
const getAdditionalLicenses = (row) => {
  const additional = get(row, 'data.company.licenseNumbers', []) || [];
  const primary = get(row, 'data.company[License #]', '');
  return (Array.isArray(additional) ? additional : [])
    .filter(ln => ln && ln !== primary)
    .join(', ');
};

// Returns the lowercased searchable text for a column on a row. Used for both the
// per-column dropdowns and the global search box.
//
// Card #277 — this used to be a local copy of the same searchValue-else-dataIndex rule
// every other table now shares. Deferring to actions/utils keeps one definition of what
// a cell "says", so this page and the rest of the dashboard can't drift apart.
const getColText = (col, row) => columnText(col, row).toLowerCase();

// Base column descriptors. `searchValue` (optional) overrides the searchable
// text when the displayed value differs from the raw dataIndex value. Full
// antd column config (sorter/filters/onFilter/filter dropdown) is layered on
// in `buildColumns` so per-column filter lists reflect the current data set.
const BASE_COLS = [
  {
    title: 'Company Name',
    dataIndex: 'data.company.Licensee',
    width: '20%',
  },
  {
    title: 'License Number',
    dataIndex: 'data.company[License #]',
    width: '13%',
  },
  {
    title: 'Additional License',
    dataIndex: 'data.company.additionalLicenseDisplay',
    width: '15%',
    searchValue: row => getAdditionalLicenses(row),
    render: (text, row) => getAdditionalLicenses(row),
  },
  {
    title: 'Address',
    dataIndex: 'data.company[Street Address]',
    width: '17%',
  },
  {
    title: 'Phone',
    dataIndex: 'data.company.phone',
    width: '10%',
  },
  {
    title: 'Email',
    dataIndex: 'email',
    width: '17%',
  },
  {
    title: 'Role',
    dataIndex: 'role',
    width: '8%',
    searchValue: row => upperFirst(get(ROLES, get(row, 'data.role'), 'User')),
    render: (text, row) => {
      const role = get(row, 'data.role');
      return (
        <div>
          {upperFirst(get(ROLES, role, 'User'))}
        </div>
      )
    }
  },
]

// Builds the full antd column config from BASE_COLS + the current data set.
// Every column gets: a sorter, a per-column searchable filter dropdown, and a
// distinct-value filter list (checkbox filters). `searchState`/`onSearch`
// drive the per-column search inputs.
const buildColumns = (cols, searchState, handlers) => {
  return cols.map(col => {
    const key = col.dataIndex;
    const searchTerm = get(searchState, key, '');

    return {
      ...col,
      sorter: (a, b) => getColText(col, a).localeCompare(getColText(col, b)),
      // Per-column searchable text box in the filter dropdown. The actual
      // filtering is applied in filterAccounts() via onColSearch state, so the
      // dropdown just drives that term (works alongside the global search box).
      filterDropdown: ({ confirm }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder={`Search ${col.title}`}
            value={searchTerm}
            onChange={e => handlers.onColSearch(key, e.target.value)}
            onPressEnter={() => confirm && confirm()}
            style={{ width: 200, marginBottom: 8, display: 'block' }}
          />
          <Button
            outline
            onClick={() => { handlers.onColSearch(key, ''); confirm && confirm(); }}
          >
            Reset
          </Button>
        </div>
      ),
      filterIcon: () => (
        <Icon type="search" style={{ color: searchTerm ? colors.highlight : undefined }} />
      ),
    };
  });
};

export class AccountsComponent extends Component {
  state = {
    allAccounts: [],
    accounts: [],
    loading: false,
    error: undefined,
    success: undefined,
    editModal: undefined,
    searchTerm: '',
    colSearch: {},
  }
  componentDidMount = () => {
    this.getAccounts();
  }
  getAccounts = async () => {
    try {
      this.setState({ loading: true });
      const accounts = await getAccounts();
      this.setState({ allAccounts: accounts, accounts: accounts.filter(a => get(a, 'data.verified')), loading: false }, () => {
        if (this.state.searchTerm) {
          this.filterAccounts();
        }
      });
    } catch (err) {
      this.setState({ loading: false, error: err.message });
    }
  }
  // Applies the base verified/pending checkbox source set, then the global
  // search box (matches any column), then each per-column search term.
  filterAccounts = () => {
    const { allAccounts, searchTerm, colSearch, showNonVerified, showPendingLicenses } = this.state;

    let source = allAccounts;
    if (showPendingLicenses) {
      source = allAccounts.filter(a =>
        get(a, 'data.company.pendingLicenseNumbers', []).length > 0 ||
        get(a, 'data.company.pendingLicenseRemovals', []).length > 0
      );
    } else if (showNonVerified) {
      source = allAccounts.filter(a => !get(a, 'data.verified'));
    } else {
      source = allAccounts.filter(a => get(a, 'data.verified'));
    }

    const global = (searchTerm || '').toLowerCase();

    const accounts = source.filter(a => {
      // Global search across all columns.
      if (global) {
        const matchesGlobal = BASE_COLS.some(col => getColText(col, a).indexOf(global) > -1);
        if (!matchesGlobal) return false;
      }
      // Per-column search terms (all must match).
      return BASE_COLS.every(col => {
        const term = (get(colSearch, col.dataIndex, '') || '').toLowerCase();
        if (!term) return true;
        return getColText(col, a).indexOf(term) > -1;
      });
    });
    this.setState({ accounts });
  }
  onColSearch = (key, value) => {
    this.setState(s => ({ colSearch: { ...s.colSearch, [key]: value } }), this.filterAccounts);
  }
  selectAccount = (record, index) => {
    const editAccount = { ...record, data: { ...get(record, 'data', {}), ...record } }
    this.setState({ editModal: { state: { editAccount } } });
  };
  onSearch = () => {
    this.filterAccounts();
  }
  onSearchChange = (e) => {
    this.setState({ searchTerm: e.target.value }, this.filterAccounts);
  }
  createAccount = () => {
    this.props.history.push('/dashboard/profile', {
      isCreate: true,
      newUser: true,
    });
  }
  onCheckChange = (e) => {
    this.setState({ showNonVerified: e.target.checked }, this.filterAccounts);
  }
  onPendingLicensesCheckChange = (e) => {
    this.setState({ showPendingLicenses: e.target.checked }, this.filterAccounts);
  }
  exportAccounts = () => {
    const rows = [BASE_COLS.map(c => c.title)];
    this.state.accounts.map(order => {
      rows.push(BASE_COLS.map(c => (c.searchValue ? c.searchValue(order) : get(order, c.dataIndex, ''))))
    })
    exportToCsv(`Accounts-${moment().toISOString()}.csv`, rows);
  }
  closeEditModal = (shouldUpdate) => {
    this.setState({ editModal: undefined });
    if (shouldUpdate) {
      this.getAccounts();
    }
  }
  render() {
    const {
      accounts,
      loading,
      editModal,
      colSearch,
    } = this.state;

    const columns = buildColumns(BASE_COLS, colSearch, { onColSearch: this.onColSearch });

    return (
      <div>
        <TableListHeader>
          <Row css={css(`width: 1000px;`)}>
            <Input.Search
              placeholder={'Global search across all columns...'}
              enterButton
              onChange={this.onSearchChange}
              onSearch={this.onSearch}
            />
            <Checkbox onChange={this.onCheckChange} css={css(SStyles.row, `margin-left: 10px;`)}>
              Show Non Verfied Users
            </Checkbox>
            <Checkbox onChange={this.onPendingLicensesCheckChange} css={css(SStyles.row, `margin-left: 10px;`)}>
              Show Pending Licenses
            </Checkbox>
          </Row>
          <Button
            onClick={this.exportAccounts}
            outline
          >
            Export accounts
          </Button>
          <Button
            onClick={this.createAccount}
          >
            <Icon type="plus" />
            Create Account
          </Button>
        </TableListHeader>
        <TableList
          data={accounts}
          loading={loading}
          columns={columns}
          rowClick={this.selectAccount}
          onRefresh={this.getAccounts}
        />
        <AccountModal editModal={editModal} onClose={this.closeEditModal} />
      </div>
    )
  }
}

export const AccountModal = props => {
  const { editModal } = props;
  const [editAccount, setEditAccount] = useState();

  const loadAccount = async (emodal) => {
    const id = get(emodal, 'state.editAccount.id');
    const user = await getUserById(id, { returnRaw: true });
    const eaccount = { ...editModal };
    set(eaccount, 'state.editAccount', user);
    setEditAccount(eaccount);
  }

  const onClose = (e) => {
    setEditAccount();
    props.onClose && props.onClose(e);
  }

  useEffect(() => {
    if (!!editModal) {
      loadAccount(editModal);
    }
  }, [editModal]);

  return (
    <Modal
      title={`Edit Account`}
      visible={!!editModal}
      width={'90%'}
      style={{ top: 30 }}
      onOk={() => onClose()}
      onCancel={() => onClose()}
      footer={(null)}
    >
      {!editAccount && <div>Loading...</div>}
      {!!editAccount && <Profile {...editAccount} onClose={onClose} isModal={true} />}
    </Modal>
  )
}

export const Accounts = connect(sState => ({ sState }))(withRouter(AccountsComponent));